/**
 * Turning the selected record into the sidebar's activity card.
 *
 * Kept as a pure function of the record: the fields are derived once per
 * record rather than recomputed on access, so the card does no work per
 * render.
 */

/** One row of the activity sub-navigation. `key` is the value `selectSection` routes on. */
export interface ActivitySection {
  key: string;
  label: string;
  icon: string;
}

/** Review states the API returns, mapped to a short label and a pill tone. */
const REVIEW_STATUS_MAP: Record<string, { label: string; tone: string }> = {
  PendingAppraiserReview: { label: 'Appraiser review', tone: 'pending' },
  PendingPortfolioReview: { label: 'Portfolio review', tone: 'pending' },
  PendingQualityReview: { label: 'Quality review', tone: 'pending' },
  Approved: { label: 'Approved', tone: 'approved' },
  Rejected: { label: 'Rejected', tone: 'rejected' }
};

const ACTIVITY_SECTION_ICONS: Record<string, string> = {
  'Subject Property': 'home_work',
  Comparables: 'compare_arrows',
  Argus: 'insights',
  ChangeHistory: 'history'
};

export interface ActivityContext {
  sections: ActivitySection[];
  recordSections: string[];
  activityNumber: string;
  propertyName: string;
  location: string;
  statusLabel: string;
  statusTone: string;
  tooltip: string;
}

export const EMPTY_ACTIVITY: ActivityContext = {
  sections: [],
  recordSections: [],
  activityNumber: '',
  propertyName: '',
  location: '',
  statusLabel: '',
  statusTone: '',
  tooltip: ''
};

function buildActivitySections(recordSections: string[]): ActivitySection[] {
  const dynamic = recordSections.map((section) => ({
    key: section,
    label: section,
    icon: ACTIVITY_SECTION_ICONS[section] ?? 'description'
  }));

  return [
    ...dynamic,
    { key: 'Comparables', label: 'Comparables', icon: ACTIVITY_SECTION_ICONS['Comparables'] },
    { key: 'Argus', label: 'Argus', icon: ACTIVITY_SECTION_ICONS['Argus'] },
    { key: 'ChangeHistory', label: 'Change History', icon: ACTIVITY_SECTION_ICONS['ChangeHistory'] }
  ];
}

/**
 * Single place where a record becomes the activity card, so the live feed and
 * the cached-record restore can never drift apart.
 */
export function buildActivityContext(record: any | null): ActivityContext {
  if (!record) {
    return EMPTY_ACTIVITY;
  }

  const location = record.subjectPropertyLocation ?? {};
  const jobCenter = record.jobCenterActivity ?? {};
  const review = REVIEW_STATUS_MAP[record.latestReview?.status];

  const activityNumber = record.activityNumber || jobCenter.activityNumber || '';
  // Property name leads: it is the human handle for the record.
  const propertyName = location.propertyName || record.propertyName || jobCenter.addressLine1 || '';
  const recordSections = ['Subject Property'];

  return {
    recordSections,
    sections: buildActivitySections(recordSections),
    activityNumber,
    propertyName,
    location: [location.city, location.state].filter(Boolean).join(', '),
    statusLabel: review?.label ?? jobCenter.statusType ?? '',
    statusTone: review?.tone ?? 'neutral',
    tooltip: [activityNumber, propertyName].filter(Boolean).join(' — ')
  };
}

/** The route a sub-section navigates to. Mirrors `selectSection` in the shell. */
export function sectionRoute(section: string, activityNumber: string): string {
  switch (section) {
    case 'Argus':
      return `/activity-details/argus/${activityNumber}`;
    case 'ChangeHistory':
      return `/activity-details/changeHistory/${activityNumber}`;
    case 'Comparables':
      return `/activity-details/comparables/${activityNumber}`;
    default:
      return `/activity-details/${activityNumber}`;
  }
}

/**
 * The sub-section and activity an `activity-details` URL points at - the
 * inverse of `sectionRoute`, or null off those routes.
 *
 * The sidebar reads its highlight from here rather than from the section
 * store, so Back and Forward move the highlight with the page. The store only
 * changes when something sets it, and Back sets nothing.
 */
export function activityRouteOf(pathname: string): { section: string; activityNumber: string } | null {
  const match = /^\/activity-details\/(?:(argus|changeHistory|comparables)\/)?([^/]+)/.exec(pathname);

  if (!match) {
    return null;
  }

  const section =
    match[1] === 'argus'
      ? 'Argus'
      : match[1] === 'changeHistory'
        ? 'ChangeHistory'
        : match[1] === 'comparables'
          ? 'Comparables'
          : 'Subject Property';

  return { section, activityNumber: decodeURIComponent(match[2]) };
}

export function getInitials(name?: string): string {
  if (!name) {
    return '';
  }

  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
